import { z } from "zod"
import { WithNewCompanyIdSchema } from "../auth/response_data"
import { GoalResponseDataSchema } from "../goals/response_data"
import { WhatsappOpeningMessagesResponseData } from "../integrations"
import { S3_UPLOAD_INPUT_FORMATS } from "../shared/files"
import { WhatsappMessageStatusUpdateEnum } from "../webhooks"
import {
  CampaignLaunchAudienceSchema,
  CampaignLaunchStatusEnum,
  CampaignLogKindEnum,
  CampaignStatusEnum,
  CampaignTriggerIntegrationSchema,
  CampaignTriggerTypeEnum,
  channelResponseSchema,
  FlowEnum,
} from "./commonTypes"

export const WorkflowEventEnum = {
  ConversationStarted: "ConversationStarted",
  ConversationFailed: "ConversationFailed",
  ConversationClosed: "ConversationClosed",
  ConversationAchieved: "ConversationAchieved",
  ConversationMessageCreated: "ConversationMessageCreated",
  ConversationTicketCreated: "ConversationTicketCreated",
  IssueCreated: "IssueCreated",
  ConversationGoalAchieved: "ConversationGoalAchieved",
  UpsellOpportunity: "UpsellOpportunity",
  CapabilityEvaluated: "CapabilityEvaluated",
  CapabilityExecuted: "CapabilityExecuted",
} as const

export const WorkflowConditionFieldEnum = {
  ConversationStatus: "ConversationStatus",
  ConversationFailureType: "ConversationFailureType",
  ConversationFlow: "ConversationFlow",
  NPSValue: "NPSValue",
  OverallSentiment: "OverallSentiment",
  CustomerAge: "CustomerAge",
  CustomerGender: "CustomerGender",
  CustomerCountry: "CustomerCountry",
  CustomerLocale: "CustomerLocale",
  CapabilityExecutionStatus: "CapabilityExecutionStatus",
  LLMPrompt: "LLMPrompt",
} as const

export const WorkflowConditionOperatorEnum = {
  Equals: "Equals",
  NotEquals: "NotEquals",
  GreaterThan: "GreaterThan",
  GreaterThanOrEqual: "GreaterThanOrEqual",
  LessThan: "LessThan",
  LessThanOrEqual: "LessThanOrEqual",
  IsNull: "IsNull",
  IsNotNull: "IsNotNull",
  ReturnsTrue: "ReturnsTrue",
  ReturnsFalse: "ReturnsFalse",
} as const

export const WorkflowConditionLogicOperatorEnum = {
  And: "And",
  Or: "Or",
} as const

export const WorkflowNodeTypeEnum = {
  Event: "Event",
  Condition: "Condition",
  Action: "Action",
} as const

export type WorkflowEvent = keyof typeof WorkflowEventEnum
export type WorkflowNodeType = keyof typeof WorkflowNodeTypeEnum

export const ActionTypeEnum = {
  CreateZendeskTicket: "CreateZendeskTicket",
  CreateGorgiasTicket: "CreateGorgiasTicket",
  CreateFrontAppTicket: "CreateFrontAppTicket",
  SendWebhook: "SendWebhook",
  SendInternalEmail: "SendInternalEmail",
  SendInConversationMessage: "SendInConversationMessage",
  TriggerCapability: "TriggerCapability",
  DisableAgent: "DisableAgent",
  UpdateCustomerTags: "UpdateCustomerTags",
} as const

export type ActionType = keyof typeof ActionTypeEnum

export const CampaignGoalResponseDataSchema = z.object({
  goal: GoalResponseDataSchema,
  shots: z.number(),
  order: z.number(),
})

export const CampaignGoalsResponseDataSchema = z.array(CampaignGoalResponseDataSchema)

export const CampaignCallbackHeaderPairSchema = z.object({
  key: z.string(),
  value: z.string(),
})

/**
 * Liquid variable paths available in the API channel callback URL. Single source of truth shared
 * by the frontend autocomplete and the backend context builder in `sendApiCallback` — keep both in
 * sync with this list.
 */
export const CAMPAIGN_CALLBACK_URL_VARIABLES = [
  "conversation.externalId",
  "conversation.source",
  "company.id",
  "company.name",
  "companyClient.externalId",
  "companyClient.name",
] as const

/** Persists as JSON on Campaign; empty array or omitted becomes null in DB. */
export const CampaignCallbackHeadersSchema = z
  .array(CampaignCallbackHeaderPairSchema)
  .nullish()
  .transform((val) => {
    if (val == null) return null
    if (val.length === 0) return null
    return val
  })

export const CampaignResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  description: z.string().nullable(),
  toneOfVoice: z.string().optional().nullable(),
  businessOpportunityDefinition: z.string().optional().nullable(),
  trigger: z.enum(CampaignTriggerTypeEnum).nullable(),
  triggerMetadata: z.unknown().nullable(),
  triggerIntegration: CampaignTriggerIntegrationSchema.nullable(),
  defaultLanguage: z.string().nullable(),
  uuid: z.string(),
  name: z.string().nullable(),
  channel: channelResponseSchema,
  whatsappNumberId: z.bigint().transform(String).nullable(),
  status: z.enum(CampaignStatusEnum),
  isSurvey: z.boolean(),
  isLogistics: z.boolean().default(false),
  shots: z.number(),
  goals: CampaignGoalsResponseDataSchema.optional(),
  actions: z.array(
    z.object({
      id: z.bigint().transform(String),
      type: z.enum(ActionTypeEnum),
      config: z.any(),
    }),
  ),
  isDefault: z.boolean(),
  flow: z.enum(FlowEnum).nullable(),
  callbackUrl: z.string().nullable(),
  callbackPayload: z.string().nullable(),
  callbackHeaders: z
    .unknown()
    .nullable()
    .transform((val): z.infer<typeof CampaignCallbackHeaderPairSchema>[] => {
      if (val == null || !Array.isArray(val)) return []
      return val.flatMap((h) => {
        const r = CampaignCallbackHeaderPairSchema.safeParse(h)
        return r.success ? [r.data] : []
      })
    }),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
  archivedAt: z
    .date()
    .transform((value) => value?.toISOString())
    .nullable(),
})

const CampaignTemplateType = {
  Whatsapp: "Whatsapp",
  Email: "Email",
} as const

export const CampaignTemplates = z.array(
  z.discriminatedUnion("channel", [
    z.object({
      channel: z.literal(CampaignTemplateType.Whatsapp),
      id: z.bigint().transform(String),
      rate: z.number().nullable(),
      active: z.boolean(),
      language: z.string(),
      name: z.string(),
      whatsappTemplateId: z.bigint().transform(String),
      status: WhatsappMessageStatusUpdateEnum,
      header: z.string().nullable(),
      body: z.string(),
      footer: z.string().nullable(),
      mediaUrl: z.string().nullable(),
      mediaFormat: S3_UPLOAD_INPUT_FORMATS.nullable(),
      whatsappTemplateButtons: z.array(z.object({ text: z.string() })),
      whatsappTemplateVariables: z.array(
        z.object({
          paramName: z.string(),
          example: z.string(),
          description: z.string().nullish(),
        }),
      ),
    }),
    z.object({
      channel: z.literal(CampaignTemplateType.Email),
      id: z.bigint().transform(String),
      rate: z.number().nullable(),
      active: z.boolean(),
      language: z.string(),
      name: z.string(),
    }),
  ]),
)

export const CampaignLaunchResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  status: z.enum(CampaignLaunchStatusEnum),
  audienceSource: CampaignLaunchAudienceSchema,
  audience: z.number(),
  closeOngoingConversations: z.boolean(),
  scheduledAt: z.date().transform((value) => value.toISOString()),
  createdAt: z.date().transform((value) => value.toISOString()),
})

export const CampaignImportResponseDataSchema = z.object({
  id: z.string().uuid(),
})

export const CampaignDetailResponseDataSchema = WithNewCompanyIdSchema.extend(
  z.object({
    ...CampaignResponseDataSchema.shape,
    isValidConfig: z.boolean(),
    scheduledLaunches: z.array(CampaignLaunchResponseDataSchema).optional(),
    pendingImports: z.array(CampaignImportResponseDataSchema).optional(),
    hasChangeLog: z.boolean().optional(),
    templates: CampaignTemplates.optional(),
    openingMessages: WhatsappOpeningMessagesResponseData.optional(),
    nodes: z.array(
      z.object({
        id: z.bigint().transform(String),
        parentId: z.bigint().transform(String).optional().nullable(),
        order: z.number(),
        createdAt: z.date().transform((value) => value.toISOString()),
        updatedAt: z.date().transform((value) => value.toISOString()),
        event: z
          .object({
            type: z.enum(WorkflowEventEnum),
            config: z.any().optional().nullable(),
          })
          .nullable(),
        conditions: z.array(
          z.object({
            field: z.enum(WorkflowConditionFieldEnum),
            operator: z.enum(WorkflowConditionOperatorEnum),
            logic: z.enum(WorkflowConditionLogicOperatorEnum).nullable(),
            value: z.string().nullable(),
            config: z.record(z.string(), z.any()).optional().nullable(),
          }),
        ),
        action: z
          .object({
            type: z.enum(ActionTypeEnum),
            config: z.any().nullable(),
          })
          .nullable(),
      }),
    ),
  }).shape,
)

export const CampaignTemplateMetricsResponseDataSchema = z.array(
  z.object({
    templateId: z.bigint().transform(String),
    timesUsed: z.number(),
    timesOpened: z.number(),
    openRate: z.number().nullable(),
    timesReplied: z.number(),
    engageRate: z.number().nullable(),
  }),
)

export const CampaignsResponseDataSchema = z.array(CampaignResponseDataSchema)

export const CampaignLogResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  createdAt: z.date().transform((value) => value.toISOString()),
  kind: z.enum(CampaignLogKindEnum),
  customerId: z.bigint().transform(String).nullable(),
  conversationId: z.bigint().transform(String).nullable(),
  employeeId: z.bigint().transform(String).nullable(),
  customer: z
    .object({
      id: z.bigint().transform(String),
      name: z.string().nullable(),
      phone: z.string().nullable(),
      email: z.string().nullable(),
      tentativeEmail: z.string().nullable(),
    })
    .nullable(),
  log: z.union([z.record(z.string(), z.any()), z.null(), z.number(), z.boolean(), z.string()]),
})

export const CampaignLogsResponseDataSchema = z.array(CampaignLogResponseDataSchema)

export const CampaignLaunchAudienceResponseDataSchema = z.object({
  audience: z.number(),
  optedOutCount: z.number(),
})

export const CampaignReachResponseDataSchema = z.object({
  audience: z.number(),
  optedOutCount: z.number(),
  reach: z.number(),
  actionableReach: z.number(),
  isEstimate: z.boolean(),
  errors: z.array(
    z.object({
      customer: z.object({
        id: z.bigint().transform(String),
        name: z.string().optional().nullable(),
      }),
      filterOutReasons: z.array(z.string()).optional(),
      noElligibleGoalIds: z.boolean().optional(),
    }),
  ),
})

export const CampaignsImpactDataSchema = z.object({
  stats: z.array(
    z.object({
      date: z
        .date()
        .nullable()
        .transform((value) => value?.toISOString()),
      reached: z.number(),
      engaged: z.number(),
      achieved: z.number(),
      insights: z.number(),
      campaigns: z.record(
        z.string(), // campaignId
        z.object({
          reached: z.number(),
          engaged: z.number(),
          achieved: z.number(),
          insights: z.number(),
        }),
      ),
    }),
  ),
  campaigns: z.array(
    z.object({
      id: z.bigint().transform(String),
      name: z.string().nullable(),
      channel: channelResponseSchema,
      trigger: z.enum(CampaignTriggerTypeEnum).nullable(),
      status: z.enum(CampaignStatusEnum),
    }),
  ),
})
